import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  isMobileBreakpoint,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/** Fixed scroll duration for every programmatic slide change (consistent autoplay speed). */
const AUTOPLAY_SCROLL_DURATION = 600;

/** @param {PointerEvent | MouseEvent} event @param {'x' | 'y'} axis */
const getPointerPosition = (event, axis) => (axis === 'x' ? event.clientX : event.clientY);

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('pointerdown', this.#handleMouseDown);
      this.#teardownMobileInfiniteSwipe();
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || this.#selecting || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    this.#selecting = true;

    try {
      await this.#selectSlide(input, event, options);
    } finally {
      this.#selecting = false;
    }
  }

  /**
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  async #selectSlide(input, event, options = {}) {

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides or invalid index
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const targetSlide = slides[index];
    if (!targetSlide || currentSlide === targetSlide) return;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const isLoopWrap =
      slides.length === 2 &&
      index !== current &&
      (requestedIndex < 0 || requestedIndex > lastIndex);
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    const isSingleStepNav = this.#getIndexStep() === 1;

    // Placeholder reorder only for multi-slide carousels (not full-width hero banners)
    if (!instant && !isAdjacentSlide && !isLoopWrap && !isSingleStepNav && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    if (instant) {
      this.#applySlideAria(index);
    } else if (this.#getIndexStep() === 1) {
      // Keep both slides painted during scroll (prevents content-visibility flash)
      slides.forEach((s) => s.setAttribute('aria-hidden', 'false'));
    }

    if (this.#scroll) {
      const scrollOptions = { instant };

      if (!instant) {
        scrollOptions.duration = AUTOPLAY_SCROLL_DURATION;
        this.#programmaticScroll = true;
      }

      await this.#scroll.to(slide, scrollOptions);
    }

    this.current = this.slides?.indexOf(slide) || 0;
    this.#applySlideAria(this.current);

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    return this.select(this.#resolveStepIndex(1), event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    return this.select(this.#resolveStepIndex(-1), event, options);
  }

  /**
   * Resolves the target slide index for a single navigation step (handles infinite wrap).
   * @param {number} direction - 1 for next, -1 for previous
   * @returns {number}
   */
  #resolveStepIndex(direction) {
    const { slides } = this;
    const lastIndex = (slides?.length ?? 0) - 1;

    if (lastIndex < 0) return 0;

    let index = this.current + direction * this.#getIndexStep();

    if (this.infinite) {
      if (index > lastIndex) index = 0;
      else if (index < 0) index = lastIndex;
    } else {
      index = clamp(index, 0, lastIndex);
    }

    return index;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#autoplayTimer != null) return;

    this.paused = false;
    this.#scheduleAutoplay(interval);
  }

  /**
   * Schedules the next autoplay tick after the configured interval.
   * @param {number} [interval]
   */
  #scheduleAutoplay(interval = this.autoplayInterval) {
    clearTimeout(this.#autoplayTimer);
    if (!interval || this.paused || !this.autoplay) return;

    this.#autoplayTimer = setTimeout(async () => {
      this.#autoplayTimer = undefined;

      if (this.paused || !this.autoplay) return;

      if (document.hidden) {
        this.#scheduleAutoplay(interval);
        return;
      }

      const hasHover = window.matchMedia('(hover: hover)').matches;
      if (hasHover && this.matches(':hover')) {
        this.#scheduleAutoplay(interval);
        return;
      }

      if (this.#selecting || this.#disabled) {
        this.#scheduleAutoplay(150);
        return;
      }

      await this.next(undefined, { animate: true });

      if (!this.paused && this.autoplay) {
        this.#scheduleAutoplay(interval);
      }
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  /**
   * Index step for next/previous — always 1 when slides are full-width (hero banners).
   * @returns {number}
   */
  #getIndexStep() {
    const { visibleSlides, slides } = this;
    const scroller = this.refs.scroller;

    if (!slides?.length || !(scroller instanceof HTMLElement) || !(slides[0] instanceof HTMLElement)) {
      return 1;
    }

    const scrollerWidth = scroller.clientWidth;
    if (!scrollerWidth) return 1;

    const slideWidth = slides[0].getBoundingClientRect().width;
    const isFullWidthSlide = slideWidth >= scrollerWidth * 0.85;

    if (isFullWidthSlide) return 1;

    return visibleSlides.length > 1 ? visibleSlides.length : 1;
  }

  get previousIndex() {
    return this.current - this.#getIndexStep();
  }

  get nextIndex() {
    return this.current + this.#getIndexStep();
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * True while a programmatic slide change is in progress (prevents autoplay overlap).
   * @type {boolean}
   */
  #selecting = false;

  /**
   * True while the scroller is being driven by select()/autoplay (not user drag).
   * @type {boolean}
   */
  #programmaticScroll = false;

  /**
   * Timeout ID for automatic playback.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #autoplayTimer = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('pointerdown', this.#handleMouseDown, { passive: false });
    this.#setupMobileInfiniteSwipe();

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#selecting || this.#disabled) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    if (!this.#selecting) {
      this.#updateVisibleSlides();
    }

    this.removeAttribute('transitioning');

    // Resource-list product carousels: let native CSS scroll-snap handle settle.
    // JS snap/clamp here fights the finger and causes flicker on mobile.
    if (
      isMobileBreakpoint() &&
      !this.#programmaticScroll &&
      !this.classList.contains('resource-list__carousel')
    ) {
      const slide = this.slides?.[this.current];
      if (slide) {
        requestAnimationFrame(() => {
          this.#snapToSlide(slide);
          this.#clampMobileScrollBounds();
        });
      }
    }

    this.#programmaticScroll = false;
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /** @type {boolean} */
  #wasSwiping = false;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #touchStartY = 0;

  /** @type {number} */
  #touchStartIndex = 0;

  /**
   * Mobile: prevent horizontal overscroll that exposes white space at slide edges.
   */
  #clampMobileScrollBounds = () => {
    if (!isMobileBreakpoint()) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll < 0) return;

    if (scroller.scrollLeft < 0) {
      scroller.scrollLeft = 0;
    } else if (scroller.scrollLeft > maxScroll) {
      scroller.scrollLeft = maxScroll;
    }
  };

  /**
   * Snap scroller exactly to a slide (fixes sub-pixel peek of adjacent slides).
   * @param {HTMLElement} slide
   */
  #snapToSlide = (slide) => {
    const { scroller } = this.refs;
    if (!scroller || !slide) return;

    const target = slide.offsetLeft;
    if (Math.abs(scroller.scrollLeft - target) > 0.5) {
      scroller.scrollLeft = target;
    }
  };

  /**
   * Mobile: wrap to last/first slide when native scroll cannot pass the edge.
   */
  #handleTouchStart = (event) => {
    if (!isMobileBreakpoint() || event.touches.length !== 1) return;

    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
    this.#touchStartIndex = this.current;
    this.#wasSwiping = false;

    if (this.autoplay) {
      this.suspend();
    }
  };

  #handleTouchMove = (event) => {
    if (!isMobileBreakpoint() || event.touches.length !== 1) return;

    const deltaX = event.touches[0].clientX - this.#touchStartX;
    const deltaY = event.touches[0].clientY - this.#touchStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance > 10) {
      this.#wasSwiping = true;
      this.setAttribute('prevent-click', '');
    }
  };

  #handleTouchEnd = async (event) => {
    if (!isMobileBreakpoint()) return;

    try {
      if (this.#wasSwiping) {
        setTimeout(() => {
          this.removeAttribute('prevent-click');
        }, 80);
      }

      if (!this.infinite) return;

      const { slides } = this;
      if (!slides?.length || slides.length <= 1) return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.#touchStartX;
      const deltaY = touch.clientY - this.#touchStartY;
      const threshold = 40;

      // Ignore vertical swipes where vertical delta is larger than horizontal delta
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;

      if (Math.abs(deltaX) < threshold) return;

      const endIndex = this.#sync();

      // A normal swipe already changed slides via scroll snap.
      if (endIndex !== this.#touchStartIndex) return;

      const slideCount = slides.length;
      let targetIndex = null;

      if (this.#touchStartIndex === 0 && deltaX > 0) {
        targetIndex = slideCount - 1;
      } else if (this.#touchStartIndex === slideCount - 1 && deltaX < 0) {
        targetIndex = 0;
      }

      if (targetIndex === null || targetIndex === this.current) return;

      await this.select(targetIndex, event, { animate: true });
    } finally {
      this.resume();
    }
  };

  #setupMobileInfiniteSwipe() {
    if (!isMobileBreakpoint()) return;

    const { scroller } = this.refs;
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    scroller.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
    scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', this.#handleTouchCancel, { passive: true });
  }

  #handleTouchCancel = () => {
    this.removeAttribute('prevent-click');
    this.resume();
  };

  #teardownMobileInfiniteSwipe() {
    const { scroller } = this.refs;
    if (!scroller) return;

    scroller.removeEventListener('touchstart', this.#handleTouchStart);
    scroller.removeEventListener('touchmove', this.#handleTouchMove);
    scroller.removeEventListener('touchend', this.#handleTouchEnd);
    scroller.removeEventListener('touchcancel', this.#handleTouchCancel);
  }

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    if (event.pointerType === 'touch') return;

    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = getPointerPosition(event, axis);

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = getPointerPosition(event, axis);
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.suspend();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const syncedIndex = this.#sync();
      const slideCount = slides.length;
      const velocityThreshold = isMobileBreakpoint() ? 5 : 10;
      const distanceThreshold = isMobileBreakpoint() ? 8 : 10;

      let modifier =
        syncedIndex !== current || Math.abs(velocity) < velocityThreshold || distanceTravelled < distanceThreshold
          ? 0
          : direction;

      // Infinite loop: wrap at first/last slide when swiping past the edge
      if (modifier === 0 && moved && distanceTravelled >= distanceThreshold && this.infinite && slideCount > 1) {
        const endDelta = startPosition - getPointerPosition(event, axis);
        if (syncedIndex === 0 && endDelta < 0) modifier = -1;
        else if (syncedIndex === slideCount - 1 && endDelta > 0) modifier = 1;
      }

      let newIndex = syncedIndex + modifier;

      if (this.infinite && slideCount > 1) {
        if (newIndex < 0) newIndex = slideCount - 1;
        else if (newIndex >= slideCount) newIndex = 0;
      } else {
        newIndex = clamp(newIndex, 0, slideCount - 1);
      }

      this.removeAttribute('dragging');
      if (moved) {
        this.setAttribute('prevent-click', '');
        setTimeout(() => {
          this.removeAttribute('prevent-click');
        }, 80);
      }
      if (event.pointerId != null) this.releasePointerCapture(event.pointerId);

      this.#scroll.snap = true;

      if (newIndex === syncedIndex) {
        this.#scroll.to(slides[syncedIndex]);
        this.resume();
        return;
      }

      await this.select(newIndex, event, { animate: true });

      if (this.#dragging) return;

      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));

        if (!this.#selecting) {
          this.#updateVisibleSlides();
        }
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Sets aria-hidden from the active slide index (stable during scroll animations).
   * @param {number} activeIndex
   */
  #applySlideAria(activeIndex) {
    const { slides } = this;
    if (!slides?.length) return;

    // Product carousels peek multiple cards — keep every slide painted to avoid flicker
    if (this.classList.contains('resource-list__carousel')) {
      slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return;
    }

    slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === activeIndex ? 'false' : 'true');
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    if (this.#selecting) return this.visibleSlides.length;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Product carousels: never hide offscreen cards via aria/content-visibility
    if (this.classList.contains('resource-list__carousel')) {
      slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return visibleSlides.length;
    }

    // Full-width heroes: use current index — IO flickers mid-transition when neither slide hits 70%.
    if (this.#getIndexStep() === 1) {
      this.#applySlideAria(this.current);
      return visibleSlides.length;
    }

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
